import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { hrApi, useAccess, useAuditVerify, useHolidays, useHrSettings, useShifts, useStaffLogins } from "../../hooks/hr";
import { useLocale } from "../../hooks/useLocale";
import type { HrLevel, HrSettings } from "../../lib/typesHr";
import { Pill, SectionHeader } from "../ui/Badges";
import { dateOnly, Field, Table, todayIso, useAction } from "./shared";

function Shifts() {
  const { t } = useTranslation();
  const shifts = useShifts();
  const { act, busy } = useAction();
  const [f, setF] = useState({ code: "", name: "", start: "09:00", end: "17:00", brk: "60" });
  return (
    <section className="card space-y-3 p-5" aria-label={t("hr.setup.shifts")}>
      <SectionHeader icon="⏰" title={t("hr.setup.shifts")} subtitle={t("hr.setup.shiftsHelp")} />
      <Table head={[t("hr.code"), t("hr.name"), t("hr.time"), t("hr.setup.break"), t("hr.setup.paidMinutes"), ""]}>
        {(shifts.data ?? []).map((s) => (
          <tr key={s.id}><td className="num font-mono font-bold">{s.code}</td><td>{s.name}</td><td className="num">{s.start_time}–{s.end_time}{s.crosses_midnight ? " ☾" : ""}</td><td className="num">{s.break_minutes}′</td><td className="num">{s.paid_minutes}′</td>
            <td className="text-end"><button type="button" className="btn-ghost !px-2 !py-0.5" disabled={busy} onClick={() => void act(() => hrApi.patch(`/shifts/${s.id}`, { active: !s.active }), "hr.done")}>{s.active ? t("hr.devices.disable") : t("hr.devices.enable")}</button></td></tr>
        ))}
      </Table>
      <div className="grid gap-2 sm:grid-cols-6">
        <input className="field num" placeholder={t("hr.code")} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} aria-label={t("hr.code")} />
        <input className="field sm:col-span-2" placeholder={t("hr.name")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} aria-label={t("hr.name")} />
        <input type="time" className="field num" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} aria-label={t("hr.from")} />
        <input type="time" className="field num" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} aria-label={t("hr.to")} />
        <button type="button" className="btn-primary" disabled={busy || !f.code.trim() || !f.name.trim()} onClick={() => void act(() => hrApi.post("/shifts", { code: f.code.trim().toUpperCase(), name: f.name.trim(), start: f.start, end: f.end, breakMinutes: Number(f.brk) || 0 }), "hr.saved").then(() => setF({ ...f, code: "", name: "" }))}>{t("hr.add")}</button>
      </div>
    </section>
  );
}

function Holidays() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const hol = useHolidays();
  const { act, busy } = useAction();
  const [day, setDay] = useState(todayIso());
  const [name, setName] = useState("");
  return (
    <section className="card space-y-3 p-5" aria-label={t("hr.setup.holidays")}>
      <SectionHeader icon="🎌" title={t("hr.setup.holidays")} />
      <ul className="space-y-1 text-xs">
        {(hol.data ?? []).map((h) => <li key={h.day} className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2 font-bold"><span><span className="num">{dateOnly(h.day, locale)}</span> · {locale === "ar" && h.name_ar ? h.name_ar : h.name}</span><button type="button" className="btn-ghost !px-2 !py-0.5 text-red-700" disabled={busy} onClick={() => void act(() => hrApi.del(`/holidays/${h.day}`), "hr.done")}>{t("hr.remove")}</button></li>)}
      </ul>
      <div className="flex gap-2">
        <input type="date" className="field num !w-40" value={day} onChange={(e) => setDay(e.target.value)} aria-label={t("hr.date")} />
        <input className="field" placeholder={t("hr.name")} value={name} onChange={(e) => setName(e.target.value)} aria-label={t("hr.name")} />
        <button type="button" className="btn-primary" disabled={busy || !name.trim()} onClick={() => void act(() => hrApi.post("/holidays", { day, name: name.trim() }), "hr.saved").then(() => setName(""))}>{t("hr.add")}</button>
      </div>
    </section>
  );
}

function Policy() {
  const { t } = useTranslation();
  const q = useHrSettings();
  const { act, busy } = useAction();
  const [s, setS] = useState<HrSettings | null>(null);
  useEffect(() => { if (q.data) setS(q.data); }, [q.data]);
  if (!s) return null;
  const bool = (k: keyof HrSettings, label: string) => (
    <label className="flex items-center justify-between gap-3 text-xs font-bold"><span>{label}</span><input type="checkbox" role="switch" aria-label={label} className="h-4 w-4" checked={s[k] as boolean} onChange={(e) => setS({ ...s, [k]: e.target.checked })} /></label>
  );
  const numField = (k: keyof HrSettings, label: string) => (
    <div className="flex items-center justify-between gap-3"><label className="text-xs font-bold" htmlFor={`hs-${k}`}>{label}</label><input id={`hs-${k}`} className="field num !w-28 text-center" inputMode="numeric" value={String(s[k] ?? "")} onChange={(e) => setS({ ...s, [k]: Number(e.target.value) })} /></div>
  );
  return (
    <section className="card space-y-3 p-5" aria-label={t("hr.setup.policy")}>
      <SectionHeader icon="📐" title={t("hr.setup.policy")} subtitle={t("hr.setup.policyHelp")} />
      {numField("late_grace_minutes", t("hr.setup.lateGrace"))}
      {bool("deduct_lateness", t("hr.setup.deductLateness"))}
      {bool("overtime_requires_approval", t("hr.setup.otApproval"))}
      {numField("overtime_min_minutes", t("hr.setup.otMin"))}
      {numField("max_total_deduction_bp", t("hr.setup.maxDeduction"))}
      {numField("loan_max_active_per_employee", t("hr.setup.loanMaxActive"))}
      {numField("loan_max_multiple_of_salary_bp", t("hr.setup.loanMultiple"))}
      {numField("loan_max_installments", t("hr.setup.loanMaxInstallments"))}
      {numField("leave_year_start_month", t("hr.setup.leaveYearStart"))}
      {bool("allow_self_approval", t("hr.setup.selfApproval"))}
      <p className="text-[10px] font-semibold text-gray-500">{t("hr.setup.bpHelp")}</p>
      <button type="button" className="btn-primary" disabled={busy} onClick={() => void act(() => hrApi.put("/settings", {
        lateGraceMinutes: s.late_grace_minutes, deductLateness: s.deduct_lateness, overtimeRequiresApproval: s.overtime_requires_approval, overtimeMinMinutes: s.overtime_min_minutes, maxTotalDeductionBp: s.max_total_deduction_bp,
        loanMaxActivePerEmployee: s.loan_max_active_per_employee, loanMaxMultipleOfSalaryBp: s.loan_max_multiple_of_salary_bp, loanMaxInstallments: s.loan_max_installments, leaveYearStartMonth: s.leave_year_start_month, allowSelfApproval: s.allow_self_approval,
      }), "hr.saved")}>{t("hr.save")}</button>
    </section>
  );
}

function Access() {
  const { t } = useTranslation();
  const access = useAccess();
  const logins = useStaffLogins();
  const { act, busy } = useAction();
  const roleOf = (id: string) => (access.data ?? []).find((a) => a.staff_id === id)?.role ?? "";
  return (
    <section className="card space-y-3 p-5" aria-label={t("hr.setup.access")}>
      <SectionHeader icon="🔑" title={t("hr.setup.access")} subtitle={t("hr.setup.accessHelp")} />
      <Table head={[t("hr.setup.login"), t("hr.setup.hrRole")]}>
        {(logins.data ?? []).filter((l) => l.role !== "owner").map((l) => (
          <tr key={l.id}><td className="font-black">{l.name} <span className="num font-mono text-[10px] text-gray-400">{l.username}</span></td>
            <td><select className="field !w-48" value={roleOf(l.id)} disabled={busy} onChange={(e) => void act(() => hrApi.put(`/access/${l.id}`, { role: e.target.value || null }), "hr.saved")} aria-label={l.name}>
              <option value="">{t("hr.setup.noHrRole")}</option>{["manager", "hr", "payroll"].map((r) => <option key={r} value={r}>{t(`hr.accessRole.${r}`)}</option>)}</select></td></tr>
        ))}
      </Table>
    </section>
  );
}

function Integrity() {
  const { t } = useTranslation();
  const v = useAuditVerify();
  return (
    <section className="card flex items-center justify-between gap-3 p-5" aria-label={t("hr.setup.audit")}>
      <div><h2 className="text-sm font-black uppercase tracking-wide">{t("hr.setup.audit")}</h2><p className="text-xs text-gray-500">{t("hr.setup.auditHelp")}</p></div>
      {v.data && <Pill tone={v.data.intact ? "green" : "red"}>{v.data.intact ? t("hr.setup.auditOk") : t("hr.setup.auditBroken", { seq: v.data.firstBrokenSeq })}</Pill>}
    </section>
  );
}

export function SetupPanel({ level }: { level: HrLevel }) {
  return (
    <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
      <div className="space-y-5"><Shifts /><Holidays /></div>
      <div className="space-y-5">{level >= 3 && <Policy />}{level >= 3 && <Access />}<Integrity /></div>
    </div>
  );
}

